import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostMarkLoaded } from "../../hooks/use-post-mark-loaded.ts";
import * as styles from "./mark-loaded-modal.module.css";

export const MarkLoadedModal = ({
	onClose,
	initialProNumber,
}: {
	onClose: () => void;
	initialProNumber?: string | null;
}) => {
	const loadedDateId = useId();
	const loadedTimeId = useId();
	const proNumberId = useId();
	const dateErrorId = `${loadedDateId}_error`;
	const timeErrorId = `${loadedTimeId}_error`;
	const futureErrorId = `${loadedDateId}_future`;
	const params = useParams();
	const { markLoaded, isSavingMarkLoaded } = usePostMarkLoaded();

	const [loadedDate, setLoadedDate] = useState<Date | null>(null);
	const [loadedTime, setLoadedTime] = useState<Date | null>(null);
	const [proNumber, setProNumber] = useState(initialProNumber ?? "");
	const [submitted, setSubmitted] = useState(false);

	// Combine the selected date and time into a single instant so it can be
	// validated against "now" and sent as a UTC ISO string.
	const completedTime =
		loadedDate && loadedTime
			? new Date(
					loadedDate.getFullYear(),
					loadedDate.getMonth(),
					loadedDate.getDate(),
					loadedTime.getHours(),
					loadedTime.getMinutes(),
					loadedTime.getSeconds(),
				)
			: null;
	const isFuture = completedTime ? completedTime.getTime() > Date.now() : false;

	const showDateError = submitted && !loadedDate;
	const showTimeError = submitted && !loadedTime;
	const showFutureError = submitted && isFuture;

	const describedBy = (fieldErrorId: string, showFieldError: boolean) =>
		[
			showFieldError ? fieldErrorId : null,
			showFutureError ? futureErrorId : null,
		]
			.filter(Boolean)
			.join(" ") || undefined;

	const handleConfirm = () => {
		setSubmitted(true);
		if (!completedTime || isFuture) {
			return;
		}
		markLoaded({
			poNumber: params["poNumber"] ?? "",
			completedTime: completedTime.toISOString(),
			proNumber: proNumber.trim() || undefined,
		});
		onClose();
	};

	return (
		<TraxModal
			className={styles.modal}
			confirmText={"Confirm Loaded"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={handleConfirm}
			showButtonsDisabled={isSavingMarkLoaded}
			title={"LTL Loaded Details"}
			type={"confirmation"}
			width={303}
		>
			<div className={styles.form}>
				<div className={styles.row}>
					<div className={`${styles.field} ${styles.dateField}`}>
						<Label editorId={loadedDateId}>
							Loaded date
							<span className={"required"}>&nbsp;*</span>
						</Label>
						<DatePicker
							ariaDescribedBy={describedBy(dateErrorId, showDateError)}
							id={loadedDateId}
							max={new Date()}
							onChange={(event) => setLoadedDate(event.target.value)}
							valid={!showDateError && !showFutureError}
							value={loadedDate}
						/>
						{showDateError ? (
							<Error id={dateErrorId}>Loaded date is required.</Error>
						) : null}
					</div>
					<div className={`${styles.field} ${styles.timeField}`}>
						{/* TimePicker puts `id` on both its wrapper and input, so a
						    `for` label would target the wrapper; label via aria instead. */}
						<Label id={`${loadedTimeId}_label`}>
							Time<span className={"required"}>*</span>
						</Label>
						<TimePicker
							ariaDescribedBy={describedBy(timeErrorId, showTimeError)}
							ariaLabelledBy={`${loadedTimeId}_label`}
							id={loadedTimeId}
							onChange={(event) => setLoadedTime(event.value)}
							valid={!showTimeError && !showFutureError}
							value={loadedTime}
						/>
						{showTimeError ? (
							<Error id={timeErrorId}>Time is required.</Error>
						) : null}
					</div>
				</div>

				{showFutureError ? (
					<Error id={futureErrorId}>
						Loaded date and time cannot be in the future.
					</Error>
				) : null}

				<div className={styles.field}>
					<Label editorId={proNumberId}>PRO Number</Label>
					<Input
						id={proNumberId}
						onChange={(event) => setProNumber(event.target.value as string)}
						value={proNumber}
					/>
				</div>
			</div>
		</TraxModal>
	);
};
